import { BoardTheme, THEME_APPLIER } from './boardTheme'

// The dashboard page runs in <iframe sandbox="allow-scripts">, so it has an opaque origin and no access to
// this app. What this document adds:
// - a policy with no network, under which only scripts carrying this render's nonce run, so script smuggled
//   in through a label can't run, and neither can inline on*= handlers;
// - no way out without a script: <meta http-equiv> goes (allow-scripts lets a refresh navigate the frame),
//   and so does <base>;
// - no way out through the nonce, which admits whatever a script loads: script src, href and xlink:href go,
//   and so does every script that is not a classic one, modules included;
// - a first script that takes WebRTC away, since the policy does not cover it;
// - the viewer's theme on <html>, and the script that switches it later (boardTheme's THEME_APPLIER).
// A hostile script that runs anyway is not contained: it can still navigate the frame with data in the
// address, call import(), or take WebRTC from a child frame. Keeping such code out is the server validators'
// job; the bridge only makes sure that a page which replaced this one never receives data.
export const dashboardContentSecurityPolicy = (nonce: string) =>
  [
    "default-src 'none'",
    `script-src 'nonce-${nonce}'`,
    "style-src 'unsafe-inline'",
    'img-src data:',
    'font-src data:',
    "form-action 'none'",
    "base-uri 'none'",
  ].join('; ')

// Scripts only: never allow-same-origin, so the page can't reach this app, and nothing that opens windows,
// submits forms or navigates the top page.
export const DASHBOARD_SANDBOX = 'allow-scripts'

export const createNonce = () => {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)

  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

export const WEBRTC_BLOCKER =
  "['RTCPeerConnection', 'webkitRTCPeerConnection', 'RTCDataChannel'].forEach(function (name) {\n" +
  '  try { delete window[name] } catch (e) {}\n' +
  '  try { Object.defineProperty(window, name, { value: undefined }) } catch (e) {}\n' +
  '})'

const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink'

// The HTML standard's JavaScript MIME types. Any other type makes a module, an import map, speculation rules
// or a data block, and a dashboard needs none of them.
const JAVASCRIPT_TYPES = new Set([
  'application/ecmascript',
  'application/javascript',
  'application/x-ecmascript',
  'application/x-javascript',
  'text/ecmascript',
  'text/javascript',
  'text/javascript1.0',
  'text/javascript1.1',
  'text/javascript1.2',
  'text/javascript1.3',
  'text/javascript1.4',
  'text/javascript1.5',
  'text/jscript',
  'text/livescript',
  'text/x-ecmascript',
  'text/x-javascript',
])

// Read the way a browser reads it: no type, or an empty one, means JavaScript, unless an old `language`
// attribute names something else.
const isClassicScript = (script: Element) => {
  const type = script.getAttribute('type')
  const language = script.getAttribute('language')
  if (type === '' || (type === null && !language)) return true

  return JAVASCRIPT_TYPES.has((type ?? `text/${language}`).trim().toLowerCase())
}

type Options = {
  // Script source added after the WebRTC blocker and the theme applier, with the same nonce: the render
  // check's probe, or the height reporter of a frame on the board.
  probe?: string
  // Light or dark is set on <html> from the start, so the page never paints in the other theme first. Auto,
  // or none, sets nothing, and the page follows the OS.
  theme?: BoardTheme
}

export default function buildSandboxDocument(html: string, { probe, theme }: Options = {}) {
  const page = new DOMParser().parseFromString(html, 'text/html')
  const nonce = createNonce()

  if (theme === 'light' || theme === 'dark') page.documentElement.setAttribute('data-theme', theme)
  page.querySelectorAll('meta[http-equiv], base').forEach(element => element.remove())

  page.querySelectorAll('script').forEach(script => {
    if (!isClassicScript(script)) {
      script.remove()

      return
    }

    script.removeAttribute('src')
    script.removeAttribute('href')
    script.removeAttribute('xlink:href')
    script.removeAttributeNS(XLINK_NAMESPACE, 'href')
    script.setAttribute('nonce', nonce)
  })

  const policy = page.createElement('meta')
  policy.setAttribute('http-equiv', 'Content-Security-Policy')
  policy.setAttribute('content', dashboardContentSecurityPolicy(nonce))
  page.head.insertBefore(policy, page.head.firstChild)

  let previous: Element = policy
  ;[WEBRTC_BLOCKER, THEME_APPLIER, probe].forEach(source => {
    if (!source) return

    const script = page.createElement('script')
    script.setAttribute('nonce', nonce)
    script.textContent = source
    page.head.insertBefore(script, previous.nextSibling)
    previous = script
  })

  return `<!DOCTYPE html>\n${page.documentElement.outerHTML}`
}
